import React, { useEffect } from "react";
import "./login.css"; // Import the CSS file for styling
import { Typography, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../user-context";
import { fetchUserRole } from "../../services/fetch-user-role";

const { Link } = Typography;

interface LoginProps {
  setIsAuthenticated: any;
  isAuthenticated: any;
  hideLoginPage: (hide: boolean) => void;
}

interface Profile {
  email: string;
  family_name: string;
  given_name: string;
  profile: Profile;
  isid: string;
  sub: string;
}

const Login: React.FC<LoginProps> = ({
  setIsAuthenticated,
  isAuthenticated,
  hideLoginPage,
}) => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory
  const { setUserRole } = useUser();

  const handleResetPassword = async () => {
    window.location.href = `${process.env.REACT_APP_PASSWORD_RESET_URL}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const getLoginDetails = sessionStorage?.getItem("profile");
    if (code) {
      hideLoginPage(false);
      exchangeCodeForToken(code)
        .then(() => {
          // Clean URL by removing query params after successful login
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        })
        .catch(() => {
          hideLoginPage(true);
        });
    } else if (getLoginDetails) {
      const authStatus = sessionStorage.getItem("isAuthenticated") === "true";
      setIsAuthenticated(authStatus);
      if (!getLoginDetails) {
        return; // Early return if no profile
      }
      const parsedLoginData: Profile = JSON.parse(getLoginDetails);
      const profile = parsedLoginData?.profile;
      const requestedEmail = profile?.email; // Get the email from the profile
      handleProfileRole(requestedEmail, profile?.isid);
    } else {
      // Handle case where no profile is found
      const authStatus = sessionStorage.getItem("isAuthenticated") === "true";
      setIsAuthenticated(authStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCodeForToken = async (code: string) => {
    const codeVerifier = localStorage.getItem("pkce_code_verifier");
    const clientId = process.env.REACT_APP_PING_ONE_CLIENT_ID;
    const tokenURL = process.env.REACT_APP_EXTERNAL_USER_TOKEN_URL;

    if (!codeVerifier) {
      throw new Error("PKCE code verifier not found in localStorage");
    }

    if (!clientId) {
      throw new Error("Missing client ID");
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      // client_id: clientId,
      code,
      redirect_uri: process.env.REACT_APP_PING_ONE_CLIENT_REDIRECT_URL ?? "",
      code_verifier: codeVerifier,
    });

    if (!tokenURL) {
      throw new Error("Missing token URL");
    }

    const response = await fetch(tokenURL, {
      method: "POST",
      headers: {
        Authorization:
          "Basic " + process.env.REACT_APP_PING_ONE_CLIENT_AUTHORIZATION_KEY,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error_description || "Token endpoint error");
    }

    const data = await response.json();
    sessionStorage.setItem("accessToken", data.access_token);
    getSupplierProfile();
    sessionStorage.setItem("refreshToken", data.refresh_token);
  };

  const getSupplierProfile = async () => {
    hideLoginPage(false);
    setIsAuthenticated(true);
    const accessToken = sessionStorage.getItem("accessToken");
    const userInfoURL = `${process.env.REACT_APP_USER_PROFILE_URL}`;
    if (!accessToken) {
      throw new Error("No access token found");
    }

    const response = await fetch(userInfoURL, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error_description || "Failed to fetch supplier profile",
      );
    }

    const data = await response.json();
    const loginUserEmail = data?.email;
    const result = await handleProfileRole(loginUserEmail, data?.isid);
    const userRoleAccess = result?.userRoleAccess;
    if (userRoleAccess) {
      setUserRole(userRoleAccess);
      data.role = userRoleAccess;
      data.organisation = result?.organisation;
      data.isid = result?.isid ?? data?.isid;

      sessionStorage.setItem("isAuthenticated", "true");
      sessionStorage.setItem("profile", JSON.stringify(data));
      navigate("/dashboard", { replace: true });
    } else {
      sessionStorage.clear();
      hideLoginPage(true);
      navigate("/no-access");
    }
  };

  const handleProfileRole = async (requestedEmail: any, isid: string) => {
    try {
      const getUserRole = await fetchUserRole(requestedEmail, isid);
      const userRoleAccess = getUserRole?.data?.user_role;
      const organisation = getUserRole?.data?.organisation ?? null;
      const userIsid = getUserRole?.data?.isid ?? null;
      return { userRoleAccess, organisation, isid: userIsid };
    } catch (err) {
      return null;
    }
  };

  const base64UrlEncode = (buffer: Uint8Array): string => {
    let binary = "";
    buffer.forEach((b) => (binary += String.fromCharCode(b)));
    const base64 = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");

    let end = base64.length;
    while (end > 0 && base64[end - 1] === "=") {
      end--;
    }

    return base64.slice(0, end);
  };

  const sha256 = async (plain: string): Promise<ArrayBuffer> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return hash;
  };

  const generateCodeChallenge = async (
    codeVerifier: string,
  ): Promise<string> => {
    const hashedBuffer = await sha256(codeVerifier);
    const hashedArray = new Uint8Array(hashedBuffer);
    return base64UrlEncode(hashedArray);
  };

  const generateCodeVerifier = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
  };

  const handleClickExternalLogin = async () => {
    const authorizeURL = process.env.REACT_APP_EXTERNAL_USER_AUTH_URL;
    const clientId = process.env.REACT_APP_PING_ONE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_PING_ONE_CLIENT_SECRET;
    const redirectUrl = process.env.REACT_APP_PING_ONE_CLIENT_REDIRECT_URL;

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    localStorage.setItem("pkce_code_verifier", codeVerifier);
    if (!clientId) {
      throw new Error("Missing client ID");
    }

    if (!clientSecret) {
      throw new Error("Missing client ID");
    }

    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId || "",
      client_secret: clientSecret || "",
      redirect_uri: redirectUrl ?? "",
      scope: "openid",
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });
    window.location.href = `${authorizeURL}?${params.toString()}`;
  };

  return isAuthenticated ? (
    <div className="spin-loader">
      <Spin />
    </div>
  ) : (
    <div className="login-container">
      <h2>Collaboration Data Ingestion Gateway</h2>
      <div className="image-container">
        <img
          src="/spot Handshake.png"
          alt="Handshake illustrating collaboration"
          className="centered-image"
        />
      </div>
      <div className="link-container">
        <Link onClick={handleClickExternalLogin} target="_blank">
          Log In
        </Link>
        <Link onClick={handleResetPassword}>
          Reset Password
        </Link>
      </div>
    </div>
  );
};

export default Login;
